import { motion } from 'framer-motion'
import { useEffect } from 'react'
import bestWayToHireBlueCollar from "../../assets/hiring_quide/best-way-to-hire-blue-collar.webp";

const challenges = [
    'Fragmented candidate supply across regions',
    'Low visibility of job opportunities among workers',
    'High drop-off rates after initial interest',
    'Limited access to digital job platforms in Tier 2 and Tier 3 cities',
    'Time-consuming screening and follow-ups',
]
const methods = [
    {
        icon: '💼',
        label: 'Job Portals',
        pros_one: 'Limited penetration for blue-collar roles',
        pros_two: 'Low response quality',
    },
    {
        icon: '🤝',
        label: 'Local Agents / Brokers',
        pros_one: 'Unstructured and inconsistent',
        pros_two: 'Lack of accountability',
    },
    {
        icon: '🚶',
        label: 'Walk-ins',
        pros_one: 'Dependent on location visibility',
        pros_two: 'Slow and unpredictable',
    },
    {
        icon: '👥',
        label: 'Referrals',
        pros_one: 'Good quality',
        pros_two: 'Low scale',
    },
]
const omnichannelFeatures = [
    {
        icon: '📱',
        text: 'Digital sourcing — job platforms, social media, WhatsApp',
    },
    {
        icon: '🏘️',
        text: 'Local outreach — field networks, registration centers',
    },
    {
        icon: '🔍',
        text: 'Assisted screening and structured follow-ups',
    },
    {
        icon: '📊',
        text: 'Centralized candidate management dashboard',
    },
]
const improvements = [
    {
        icon: '⚡',
        text: 'Speed of hiring',
    },
    {
        icon: '⭐',
        text: 'Candidate quality',
    },
    {
        icon: '📈',
        text: 'Conversion rates',
    },
    {
        icon: '🌍',
        text: 'Geographic reach',
    },
]
const keyAdvantages = [
    'Access to 14 lakh+ job seekers',
    'Sourcing through digital + offline channels',
    'Assisted hiring (not just self-service)',
    'Strong presence in Tier 2 and Tier 3 cities',
    'Faster turnaround through structured workflows',
]
const differentiators = [
    'Not just a job listing platform',
    'Not just a consultancy',
    'A combined hiring system built for MSMEs',
]
const whenToUse = [
    'You need to hire 1 or N number of candidates',
    'You are hiring in multiple locations',
    'You need fast turnaround (within days)',
    'With or without a dedicated HR team',
]
const roles = [
    'Drivers',
    'Machine operator',
    'Helpers',
    'Delivery Executives',
    'Sales Associates',
]
const futureItems = [
    {
        icon: '🤖',
        title: 'AI Matching',
        desc: 'Smart algorithms match the right candidate to the right role instantly',
    },
    {
        icon: '🌐',
        title: 'Digital Sourcing',
        desc: 'Reaching workers where they are — WhatsApp, IVR, mobile-first tools',
    },
    {
        icon: '🏙️',
        title: 'Local Infrastructure',
        desc: 'Field networks and assisted onboarding for Tier 2 & 3 cities',
    },
    {
        icon: '💬',
        title: 'Faster communication',
        desc: 'Platforms that integrate these elements will dominate hiring outcomes',
    },
]
const problems = [
    'Traditional hiring fails because it depends on single-channel sourcing',
    'Job portals miss non-digital candidates',
    'Local hiring lacks scale',
    "Referrals can't support volume hiring",
]
// Animation variants
const fadeInUp = {
    hidden: {
        opacity: 0,
        y: 32,
    },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.7,
            ease: 'easeOut',
        },
    },
}
const staggerContainer = {
    hidden: {
        opacity: 0,
    },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.15,
        },
    },
}
const staggerItem = {
    hidden: {
        opacity: 0,
        y: 20,
    },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.5,
            ease: 'easeOut',
        },
    },
}
export const BestWayToHireBlueCollar = () => {
    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])
    return (
        <section
            className="relative bg-white overflow-hidden text-gray-900"
            style={{
                fontFamily: "'Georgia', serif",
            }}
        >
            <style>{`
              @keyframes moveUp {
                0%, 100% { transform: translateY(0px); }
                50% { transform: translateY(-12px); }
              }
            `}</style>
            {/* Decorative Background Vectors */}
            <svg
                className="absolute top-0 left-0 w-96 h-96 pointer-events-none opacity-20 -translate-x-1/2 -translate-y-1/2"
                viewBox="0 0 200 200"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    fill="#fa6011"
                    d="M44.7,-76.4C58.9,-69.2,71.8,-59.1,81.6,-46.3C91.4,-33.5,98.1,-18.1,97.6,-3.1C97.1,11.9,89.4,26.6,79.4,38.9C69.4,51.2,57.1,61.1,43.4,68.5C29.7,75.9,14.8,80.8,0.1,80.6C-14.6,80.4,-29.2,75.1,-42.2,67.3C-55.2,59.5,-66.6,49.2,-75.4,36.5C-84.2,23.8,-90.4,8.7,-89.6,-5.9C-88.8,-20.5,-81,-34.6,-71.1,-46.1C-61.2,-57.6,-49.2,-66.5,-36.1,-74.1C-23,-81.7,-8.8,-88,3.3,-92.3C15.4,-96.6,30.5,-83.6,44.7,-76.4Z"
                    transform="translate(100 100)"
                />
            </svg>
            <svg
                className="absolute bottom-0 right-0 w-96 h-96 pointer-events-none opacity-20 translate-x-1/3 translate-y-1/3"
                viewBox="0 0 200 200"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    fill="#fa6011"
                    d="M47.7,-73.4C60.4,-65.4,68.4,-50.2,75.3,-34.8C82.2,-19.4,88,-3.8,85.4,10.6C82.8,25,71.8,38.2,59.2,49.1C46.6,60,32.4,68.6,16.8,74.5C1.2,80.4,-15.8,83.6,-31.1,79.1C-46.4,74.6,-60,62.4,-70.6,48.1C-81.2,33.8,-88.8,17.4,-89.1,0.8C-89.4,-15.8,-82.4,-32.6,-71.4,-45.6C-60.4,-58.6,-45.4,-67.8,-30.8,-74.1C-16.2,-80.4,-2,-83.8,12.8,-81.4C27.6,-79,42.4,-70.8,47.7,-73.4Z"
                    transform="translate(100 100)"
                />
            </svg>

            <div className="relative z-10 max-w-5xl mx-auto px-6 py-16">
                {/* ── Hero ── */}
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{
                        once: true,
                        margin: '-50px',
                    }}
                    variants={fadeInUp}
                    className="text-center pt-10 pb-16"
                >
                    <h1
                        className="text-4xl md:text-6xl font-bold leading-tight mb-6"
                        style={{
                            color: '#fa6011',
                        }}
                    >
                        The Best Way to Hire Blue-Collar Workers in India
                        <br />
                    </h1>
                    <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
                        Employers who rely only on job portals or referrals often face low
                        response rates, poor candidate quality, and high drop-offs. Here's
                        how to fix that.
                    </p>

                    {/* Animated Hero Illustration */}
                    <div className="mt-10 flex justify-center">
                        <img
                            src={bestWayToHireBlueCollar}
                            alt="Best way to hire blue-collar workers — Digital and on-ground recruitment through online job portals and local outreach"
                            className="w-full max-w-2xl rounded-2xl shadow-lg"
                            style={{
                                animation: 'moveUp 5s ease-in-out infinite',
                            }}
                        />
                    </div>
                </motion.div>

                {/* ── The Real Problem ── */}
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{
                        once: true,
                        margin: '-50px',
                    }}
                    variants={fadeInUp}
                    className="mb-20"
                >
                    <div
                        className="rounded-3xl p-8 md:p-12"
                        style={{
                            background: 'linear-gradient(135deg, #fff8f4 0%, #fff3ec 100%)',
                            border: '2px solid #fddcc8',
                        }}
                    >
                        <h2
                            className="text-2xl md:text-3xl font-bold mb-3"
                            style={{
                                color: '#fa6011',
                            }}
                        >
                            It's Not a Supply Problem — It's a Matching Problem
                        </h2>
                        <p className="text-gray-700 text-lg leading-relaxed mb-8">
                            Even if candidates exist, reaching the right candidates at the
                            right time is difficult. The gap is not talent availability — it
                            is <strong>distribution and access.</strong>
                        </p>

                        {/* Challenge grid */}
                        <motion.div
                            variants={staggerContainer}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{
                                once: true,
                            }}
                            className="grid grid-cols-1 md:grid-cols-2 gap-4"
                        >
                            {challenges.map((c, i) => (
                                <motion.div
                                    key={i}
                                    variants={staggerItem}
                                    className={`flex items-start gap-3 bg-white rounded-2xl px-5 py-4 shadow-sm ${challenges.length % 2 !== 0 && i === challenges.length - 1 ? 'md:col-span-2 md:justify-center md:max-w-md md:mx-auto' : ''}`}
                                >
                  <span
                      className="mt-1 flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                      style={{
                          background: '#fa6011',
                      }}
                  >
                    {i + 1}
                  </span>
                                    <p className="text-gray-700 text-base leading-relaxed">{c}</p>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                </motion.div>

                {/* ── How Companies Hire Today ── */}
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{
                        once: true,
                        margin: '-50px',
                    }}
                    variants={fadeInUp}
                    className="mb-20"
                >
                    <h2
                        className="text-3xl md:text-4xl font-bold text-center mb-3"
                        style={{
                            color: '#1a1a1a',
                        }}
                    >
                        What are the common ways companies hire today?
                    </h2>
                    <p className="text-center text-gray-500 mb-10">
                        Most MSMEs use a mix of the following methods
                    </p>
                    <motion.div
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{
                            once: true,
                        }}
                        className="grid grid-cols-1 sm:grid-cols-2 gap-6"
                    >
                        {methods.map((m, i) => (
                            <motion.div
                                key={i}
                                variants={staggerItem}
                                className="rounded-2xl border bg-white p-6 shadow-sm hover:shadow-md transition-shadow"
                                style={{
                                    borderColor: '#f0f0f0',
                                }}
                            >
                                <div className="text-3xl mb-3">{m.icon}</div>
                                <h3
                                    className="font-bold text-lg mb-2"
                                    style={{
                                        color: '#1a1a1a',
                                    }}
                                >
                                    {m.label}
                                </h3>
                                <p className="text-sm font-semibold uppercase tracking-wide mb-1" style={{ color: '#f87171' }}>
                                    ✓ {m.pros_one}
                                </p>
                                <p className="text-sm font-semibold uppercase tracking-wide mb-1" style={{ color: '#f87171' }}>
                                    ✓ {m.pros_two}
                                </p>
                            </motion.div>
                        ))}
                    </motion.div>
                </motion.div>

                {/* ── Omnichannel Section ── */}
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{
                        once: true,
                        margin: '-50px',
                    }}
                    variants={fadeInUp}
                    className="mb-20"
                >
                    <div
                        className="rounded-3xl overflow-hidden"
                        style={{
                            background: 'linear-gradient(135deg, #fff8f4 0%, #fff3ec 100%)',
                            border: '2px solid #fddcc8',
                        }}
                    >
                        <div className="p-8 md:p-12">
                            <h2
                                className="text-3xl md:text-4xl font-bold mb-4"
                                style={{
                                    color: '#fa6011',
                                }}
                            >
                                What is the most effective hiring approach?
                            </h2>
                            <p className="text-gray-700 text-lg mb-10 leading-relaxed">
                                The most effective approach is an omnichannel recruitment model.
                                This includes
                            </p>

                            <motion.div
                                variants={staggerContainer}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{
                                    once: true,
                                }}
                                className="grid grid-cols-1 md:grid-cols-2 gap-4"
                            >
                                {omnichannelFeatures.map((f, i) => (
                                    <motion.div
                                        key={i}
                                        variants={staggerItem}
                                        className="flex items-center gap-4 rounded-2xl px-5 py-4 bg-white shadow-sm"
                                        style={{
                                            border: '1px solid #fddcc8',
                                        }}
                                    >
                                        <span className="text-2xl">{f.icon}</span>
                                        <p className="text-gray-700 text-base">{f.text}</p>
                                    </motion.div>
                                ))}
                            </motion.div>

                            <h2
                                className="text-3xl md:text-4xl font-bold text-center mb-3 mt-12"
                                style={{
                                    color: '#1a1a1a',
                                }}
                            >
                                This model improves
                            </h2>

                            {/* Benefits row */}
                            <motion.div
                                variants={staggerContainer}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{
                                    once: true,
                                }}
                                className="grid grid-cols-1 md:grid-cols-2 gap-4"
                            >
                                {improvements.map((f, i) => (
                                    <motion.div
                                        key={i}
                                        variants={staggerItem}
                                        className={`flex flex-col items-center text-center gap-2 rounded-2xl px-5 py-4 bg-white shadow-sm ${improvements.length % 2 !== 0 && i === improvements.length - 1 ? 'md:col-span-2 md:max-w-md md:mx-auto' : ''}`}
                                        style={{
                                            border: '1px solid #fddcc8',
                                        }}
                                    >
                                        <span className="text-2xl">{f.icon}</span>
                                        <p className="text-gray-700 text-base font-medium">{f.text}</p>
                                    </motion.div>
                                ))}
                            </motion.div>
                        </div>
                    </div>
                </motion.div>

                {/* ── Kaigal Section ── */}
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{
                        once: true,
                        margin: '-50px',
                    }}
                    variants={fadeInUp}
                    className="mb-20"
                >
                    {/* Header */}
                    <h2
                        className="text-3xl md:text-4xl font-bold text-center mb-2"
                        style={{
                            color: '#fa6011',
                        }}
                    >
                        How Kaigal Helps You Hire Faster
                    </h2>
                    <p className="text-center text-gray-500 mb-12 text-lg">
                        Not just a job listing platform. Not just a consultancy.
                        <br />A combined hiring system built for MSMEs.
                    </p>

                    {/* Key Advantages */}
                    <section className="mb-14">
                        <h2 className="text-2xl md:text-3xl font-bold mb-6">
                            Key advantages
                        </h2>

                        <motion.div
                            variants={staggerContainer}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{
                                once: true,
                            }}
                            className="grid grid-cols-1 md:grid-cols-2 gap-5"
                        >
                            {keyAdvantages.map((text, i) => (
                                <motion.div
                                    key={i}
                                    variants={staggerItem}
                                    className={`rounded-2xl px-6 py-6 bg-white shadow-sm flex items-start gap-4 hover:shadow-md transition ${keyAdvantages.length % 2 !== 0 && i === keyAdvantages.length - 1 ? 'md:col-span-2 md:max-w-md md:mx-auto' : ''}`}
                                    style={{
                                        border: '1px solid #fddcc8',
                                    }}
                                >
                  <span
                      className="mt-1 flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full text-white text-sm font-bold"
                      style={{
                          background: '#fa6011',
                      }}
                  >
                    {i + 1}
                  </span>
                                    <p className="text-gray-700 text-base">{text}</p>
                                </motion.div>
                            ))}
                        </motion.div>
                    </section>

                    {/* Differentiators */}
                    <section className="mb-14">
                        <h2 className="text-2xl md:text-3xl font-bold mb-6">
                            What makes Kaigal different
                        </h2>

                        <motion.div
                            variants={staggerContainer}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{
                                once: true,
                            }}
                            className="grid grid-cols-1 md:grid-cols-3 gap-5"
                        >
                            {differentiators.map((text, i) => (
                                <motion.div
                                    key={i}
                                    variants={staggerItem}
                                    className="rounded-2xl px-6 py-6 bg-white shadow-sm text-center hover:shadow-md transition"
                                    style={{
                                        border: '1px solid #fddcc8',
                                    }}
                                >
                                    <p className="text-gray-700 text-base font-medium">{text}</p>
                                </motion.div>
                            ))}
                        </motion.div>
                    </section>

                    {/* Bottom Two Column Section */}
                    <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* When to Use */}
                        <motion.div
                            variants={fadeInUp}
                            className="rounded-2xl p-7"
                            style={{
                                background: 'rgba(255, 243, 236, 0.6)',
                                border: '2px solid #fddcc8',
                            }}
                        >
                            <h3 className="font-bold text-xl mb-5">When to Use Kaigal</h3>

                            <ul className="space-y-3">
                                {whenToUse.map((w, i) => (
                                    <li key={i} className="flex items-start gap-3">
                    <span
                        className="mt-1 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-white text-xs"
                        style={{
                            background: '#fa6011',
                        }}
                    >
                      ✓
                    </span>
                                        <span className="text-gray-700 text-base">{w}</span>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>

                        {/* Roles */}
                        <motion.div
                            variants={fadeInUp}
                            className="rounded-2xl p-7"
                            style={{
                                background: 'rgba(255, 243, 236, 0.6)',
                                border: '2px solid #fddcc8',
                            }}
                        >
                            <h3 className="font-bold text-xl mb-5">Roles you can hire for</h3>

                            <ul className="space-y-3">
                                {roles.map((r, i) => (
                                    <li key={i} className="flex items-start gap-3">
                    <span
                        className="mt-1 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-white text-xs"
                        style={{
                            background: '#fa6011',
                        }}
                    >
                      ✓
                    </span>
                                        <span className="text-gray-700 text-base">{r}</span>
                                    </li>
                                ))}
                            </ul>

                            <p className="text-gray-500 text-sm mt-6 text-left">
                                From sourcing to screening, Kaigal helps you hire 80+ skilled candidates—online and on-ground.
                            </p>
                        </motion.div>
                    </section>
                </motion.div>

                {/* ── Why Traditional Methods Fail ── */}
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{
                        once: true,
                        margin: '-50px',
                    }}
                    variants={fadeInUp}
                    className="mb-20"
                >
                    <div
                        className="rounded-3xl p-8 md:p-12 text-center"
                        style={{
                            background: 'linear-gradient(135deg, #fff3ec, #ffeedd)',
                            border: '2px dashed #fbbf9a',
                        }}
                    >
                        <h2
                            className="text-3xl md:text-4xl font-bold mb-4"
                            style={{
                                color: '#fa6011',
                            }}
                        >
                            Why Traditional Hiring Fails
                        </h2>
                        <div className="max-w-2xl mx-auto mb-8 text-left md:text-center">
                            <ul className="space-y-3 inline-block text-left">
                                {problems.map((text, i) => (
                                    <li key={i} className="flex items-start gap-3">
                    <span
                        className="mt-1 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-white text-xs"
                        style={{
                            background: '#fa6011',
                        }}
                    >
                      ✓
                    </span>
                                        <span className="text-gray-700 text-lg">
                      {i === 0 ? (
                          <>
                              Traditional hiring fails because it depends on{' '}
                              <strong>single-channel sourcing</strong>
                          </>
                      ) : (
                          text
                      )}
                    </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div
                            className="inline-block rounded-2xl px-8 py-5 bg-white shadow-md"
                            style={{
                                border: '1px solid #fddcc8',
                            }}
                        >
                            <p
                                className="text-xl font-bold"
                                style={{
                                    color: '#1a1a1a',
                                }}
                            >
                                "The gap is not talent availability —{' '}
                                <span
                                    style={{
                                        color: '#fa6011',
                                    }}
                                >
                  it is distribution and access."
                </span>
                            </p>
                        </div>
                    </div>
                </motion.div>

                {/* ── Future of Hiring (Timeline) ── */}
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{
                        once: true,
                        margin: '-50px',
                    }}
                    variants={fadeInUp}
                    className="mb-20"
                >
                    <h2
                        className="text-3xl md:text-4xl font-bold text-center mb-10"
                        style={{
                            color: '#1a1a1a',
                        }}
                    >
                        The Future of Blue-Collar Hiring
                    </h2>

                    <div className="relative">
                        {/* Vertical line */}
                        <div
                            className="absolute left-6 top-0 bottom-0 w-0.5 hidden md:block"
                            style={{
                                background: 'linear-gradient(to bottom, #fa6011, #fddcc8)',
                            }}
                        />
                        <motion.div
                            variants={staggerContainer}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{
                                once: true,
                            }}
                            className="space-y-8"
                        >
                            {futureItems.map((f, i) => (
                                <motion.div
                                    key={i}
                                    variants={staggerItem}
                                    className="flex gap-6 items-start relative"
                                >
                                    <div
                                        className="hidden md:flex flex-shrink-0 w-12 h-12 rounded-full items-center justify-center text-xl shadow-md z-10"
                                        style={{
                                            background: 'white',
                                            border: '3px solid #fa6011',
                                        }}
                                    >
                                        {f.icon}
                                    </div>
                                    <div
                                        className="flex-1 rounded-2xl p-6 bg-white shadow-sm"
                                        style={{
                                            border: '1px solid #f0f0f0',
                                        }}
                                    >
                                        <h3
                                            className="font-bold text-lg mb-1"
                                            style={{
                                                color: '#1a1a1a',
                                            }}
                                        >
                                            {f.title}
                                        </h3>
                                        <p className="text-gray-600 text-base leading-relaxed">{f.desc}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                </motion.div>

                {/* ── Conclusion / CTA ── */}
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{
                        once: true,
                        margin: '-50px',
                    }}
                    variants={fadeInUp}
                >
                    <div
                        className="rounded-3xl text-center p-10 md:p-16"
                        style={{
                            background: 'linear-gradient(135deg, #fff8f4 0%, #fff3ec 100%)',
                            border: '2px solid #fddcc8',
                        }}
                    >
                        <h2
                            className="text-3xl md:text-4xl font-bold mb-4"
                            style={{ color: '#fa6011' }}
                        >
                            Ready to Hire Smarter?
                        </h2>

                        <p className="text-gray-800 text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
                            For employers in India, the best way to hire blue-collar workers is to use an omnichannel recruitment approach that combines technology with local execution. Kaigal is built to deliver this at scale.
                        </p>

                        <a
                            href="https://recruit.kaigal.com/?utm_source=website&utm_medium=blog&utm_campaign=why_msme_struggle_to_hire_blue_collars#/signupcheck"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block font-bold text-white px-10 py-4 rounded-full text-lg shadow-lg transition-transform hover:scale-105"
                            style={{ background: '#fa6011' }}
                        >
                            Start Hiring with Kaigal →
                        </a>
                    </div>
                </motion.div>
            </div>
        </section>
    )
}
